import React, { useMemo } from 'react';

/**
 * EstimatSummaryBox - Översikt över estimatets totalsummor
 * 
 * 🚀 OPTIMERING: Använder useMemo för att cacha beräkningar
 * 
 * Visar:
 * - Projektering (inkl. baspaket-andel)
 * - Byggoffert (inkl. baspaket-andel)
 * - Etablering, avfall & admin (inkl. baspaket-andel)
 * - Sidoentreprenörer (inkl. baspaket-andel)
 * - Inköp fast inredning (inkl. baspaket-andel)
 * - ROT-avdrag
 * - Totalsumma och moms
 * - "Varav baspaket (fördelat)" - endast synlig för byggkonsulter
 */
const EstimatSummaryBox = ({ estimat, projectInfo, isRotBeloppEditable, calculateSectionTotals }) => {
  // 🚀 OPTIMERING: Cacha calculateSectionTotals resultat
  const s = useMemo(
    () => calculateSectionTotals(estimat, projectInfo, isRotBeloppEditable),
    [estimat, projectInfo, isRotBeloppEditable, calculateSectionTotals]
  );

  // 🚀 OPTIMERING: Cacha rows array så den inte byggs varje render
  // Använder nu *InklBaspaket-värden som inkluderar proportionellt fördelat baspaket
  const rows = useMemo(() => {
    const tempRows = [];
    const hasBaspaket = s.inredningsHjalp > 0;
    const bf = s.baspaketFordelning || {};
    
    // Hjälpfunktion för att beräkna procent av baspaket
    const getBaspaketPercent = (amount) => {
      if (!hasBaspaket || s.inredningsHjalp === 0) return 0;
      return Math.round((amount / s.inredningsHjalp) * 100);
    };
    
    // Bygg upp raderna dynamiskt baserat på vad som har värde
    // Använd InklBaspaket-värden för att visa korrekta summor med fördelat baspaket
    if (s.projekteringInklBaspaket > 0) {
      tempRows.push({ 
        label: 'Projektering', 
        value: s.projekteringInklBaspaket,
        originalValue: s.projektering,
        baspaketAmount: bf.projektering || 0,
        baspaketPercent: getBaspaketPercent(bf.projektering || 0)
      });
    }
    
    if (s.byggoffertInklBaspaket > 0 || s.etableringInklBaspaket > 0 || s.byggoffertROT > 0) {
      // Byggoffert visas som EN post inkl. etablering/avfall/admin (som beräknas
      // som % av byggofferten men slås ihop i översikten).
      const baspaketAmount = (bf.byggoffert || 0) + (bf.etablering || 0);
      tempRows.push({
        label: 'Byggoffert',
        value: s.byggoffertInklBaspaket + s.etableringInklBaspaket,
        originalValue: s.byggoffert + s.byggoffertEtablering,
        baspaketAmount,
        baspaketPercent: getBaspaketPercent(baspaketAmount)
      });
    }
    
    if (s.sidoentreprenorerInklBaspaket > 0) {
      tempRows.push({ 
        label: 'Sidoentreprenörer/specialister', 
        value: s.sidoentreprenorerInklBaspaket,
        originalValue: s.sidoentreprenorer,
        baspaketAmount: bf.sidoentreprenorer || 0,
        baspaketPercent: getBaspaketPercent(bf.sidoentreprenorer || 0)
      });
    }
    
    if (s.inkopFastInredningInklBaspaket > 0) {
      tempRows.push({ 
        label: 'Inköp fast inredning', 
        value: s.inkopFastInredningInklBaspaket,
        originalValue: s.inkopFastInredning,
        baspaketAmount: bf.inkopFastInredning || 0,
        baspaketPercent: getBaspaketPercent(bf.inkopFastInredning || 0)
      });
    }
    
    // Lägg till "Varav baspaket (fördelat)" SIST om det finns baspaket
    // Endast synlig för byggkonsulter - kunden ser aldrig denna rad
    if (hasBaspaket) {
      tempRows.push({ 
        label: 'Varav baspaket (fördelat)', 
        value: s.inredningsHjalp,
        isBaspaket: true 
      });
    }
    
    return tempRows;
  }, [
    s.projekteringInklBaspaket, 
    s.projektering,
    s.byggoffertInklBaspaket, 
    s.byggoffert,
    s.etableringInklBaspaket, 
    s.byggoffertEtablering,
    s.byggoffertROT, 
    s.sidoentreprenorerInklBaspaket, 
    s.sidoentreprenorer,
    s.inkopFastInredningInklBaspaket, 
    s.inkopFastInredning,
    s.inredningsHjalp,
    s.baspaketFordelning
  ]);

  // 🚀 OPTIMERING: Cacha totalsummor - använd vatBase som redan är korrekt beräknad
  const { totalExclRot, customerTotal, vatAmount } = useMemo(() => ({
    totalExclRot: s.vatBase, // vatBase innehåller alla kategorier inkl. baspaket
    customerTotal: s.vatBase - s.byggoffertROT,
    vatAmount: Math.round(s.vatBase * (25 / 125))
  }), [s.vatBase, s.byggoffertROT]);

  // Om inga rader finns, visa inget
  if (rows.length === 0) return null;

  return (
    <div className="mt-4 bg-blue-50 rounded-md border border-blue-200 p-3 sm:p-4">
      <div className="text-sm font-medium text-gray-800 mb-2">Översikt:</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className={`text-gray-700 ${r.emphasize ? 'font-semibold' : ''} ${(r.label === 'Etablering, avfall & admin' || r.isBaspaket) ? 'italic ml-4' : ''}`}>
              {r.label}
            </span>
            <span className="relative group flex items-center">
              <span className={`tabular-nums ${r.value < 0 ? 'text-red-600' : 'text-gray-900'} ${r.emphasize ? 'font-semibold' : ''} ${r.isBaspaket ? 'italic' : ''}`}>
                {r.value.toLocaleString('sv-SE')} kr
              </span>
              {/* Info-ikon för baspaket-fördelning - visas om posten har baspaket-andel */}
              {r.baspaketAmount > 0 && !r.isBaspaket && (
                <>
                  <button
                    type="button"
                    className="ml-1.5 text-amber-500 hover:text-amber-600"
                    aria-label={`Baspaket-fördelning för ${r.label}`}
                    tabIndex={0}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                      <path d="M10.75 10.818v2.614A3.13 3.13 0 0011.888 13c.482-.315.612-.648.612-.875 0-.227-.13-.56-.612-.875a3.13 3.13 0 00-1.138-.432zM8.33 8.62c.053.055.115.11.184.164.208.16.46.284.736.363V6.603a2.45 2.45 0 00-.35.13c-.14.065-.27.143-.386.233-.377.292-.514.627-.514.909 0 .184.058.39.202.592.037.051.08.102.128.152z" />
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-6a.75.75 0 01.75.75v.316a3.78 3.78 0 011.653.713c.426.33.744.74.925 1.2a.75.75 0 01-1.395.55 1.35 1.35 0 00-.447-.563 2.187 2.187 0 00-.736-.363V9.3c.698.093 1.383.32 1.959.696.787.514 1.29 1.27 1.29 2.13 0 .86-.504 1.616-1.29 2.13-.576.377-1.261.603-1.96.696v.299a.75.75 0 11-1.5 0v-.3c-.697-.092-1.382-.318-1.958-.695-.482-.315-.857-.717-1.078-1.188a.75.75 0 111.359-.636c.08.173.245.376.54.569.313.205.706.353 1.138.432v-2.748a3.782 3.782 0 01-1.653-.713C6.9 9.433 6.5 8.681 6.5 7.875c0-.805.4-1.558 1.097-2.096a3.78 3.78 0 011.653-.713V4.75A.75.75 0 0110 4z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <span className="pointer-events-none absolute right-0 -top-16 bg-gray-900 text-white text-[10px] px-2.5 py-2 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 leading-relaxed">
                    <span className="block">Original: {r.originalValue.toLocaleString('sv-SE')} kr</span>
                    <span className="block">Baspaket: +{r.baspaketAmount.toLocaleString('sv-SE')} kr</span>
                    <span className="block">Andel: {r.baspaketPercent}%</span>
                  </span>
                </>
              )}
              {r.label.startsWith('ROT-avdrag') && (
                <>
                  <button
                    type="button"
                    className="ml-2 text-gray-400 hover:text-gray-600"
                    aria-label="Visa ROT‑maxtak"
                    title="ROT‑maxtak"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 100 2 1 1 0 000-2zm-2 7a1 1 0 102 0v-3a1 1 0 10-2 0v3z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <span className="pointer-events-none absolute right-0 -top-8 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    ROT‑maxtak: {s.byggoffertRotMax.toLocaleString('sv-SE')} kr
                  </span>
                </>
              )}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 mt-3 pt-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-gray-800 font-semibold">Total</span>
          <span className="text-gray-900 tabular-nums">{totalExclRot.toLocaleString('sv-SE')} kr</span>
        </div>
        {s.byggoffertROT > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-gray-700">ROT‑avdrag{s.rotAntal > 0 ? ` (${s.rotAntal} st)` : ''}</span>
            <span className="relative group flex items-center">
              <span className="text-green-600 tabular-nums">- {s.byggoffertROT.toLocaleString('sv-SE')} kr</span>
              <button
                type="button"
                className="ml-2 text-gray-400 hover:text-gray-600"
                aria-label="Visa ROT‑maxtak"
                title="ROT‑maxtak"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 100 2 1 1 0 000-2zm-2 7a1 1 0 102 0v-3a1 1 0 10-2 0v3z" clipRule="evenodd" />
                </svg>
              </button>
              <span className="pointer-events-none absolute right-0 -top-8 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                ROT‑maxtak: {s.byggoffertRotMax.toLocaleString('sv-SE')} kr
              </span>
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-gray-800 font-semibold">Estimerat att betala</span>
          <span className="text-gray-900 font-bold text-base sm:text-lg tabular-nums">{customerTotal.toLocaleString('sv-SE')} kr</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600 italic">varav moms (25%)</span>
          <span className="text-gray-900 tabular-nums">{vatAmount.toLocaleString('sv-SE')} kr</span>
        </div>
      </div>
    </div>
  );
};

export default EstimatSummaryBox;
